// config/axiosConfig.js - Axios Interceptors with Authorization Handling

import axios from 'axios';
import { clearAuthData, isAuthenticationError, isAuthorizationError } from '../utils/authUtils';

// Create axios instance
const apiClient = axios.create();

// Request interceptor to add auth headers
apiClient.interceptors.request.use(
    (config) => {
        // Add auth token to requests
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Add user ID to request headers for backend authorization checks
        const authUser = localStorage.getItem('authUser');
        if (authUser) {
            try {
                const user = JSON.parse(authUser);
                if (user.userId) {
                    config.headers['X-User-ID'] = user.userId;
                }
            } catch (error) {
                console.error('Error parsing auth user:', error);
            }
        }

        // Log request for debugging (remove in production)
        if (process.env.NODE_ENV === 'development') {
            console.log('API Request:', {
                method: config.method?.toUpperCase(),
                url: config.url,
                hasAuth: !!config.headers.Authorization,
                hasUserId: !!config.headers['X-User-ID']
            });
        }

        return config;
    },
    (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Token refresh functionality
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

// Unified authentication handler for all environments
const handleAuthenticationError = async (error, originalRequest) => {
    // Detect environment
    const isLocalDevelopment = window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';
    const isProduction = window.location.hostname.includes('intouchwe.com');

    console.log('Environment detection:', {
        isLocalDevelopment,
        isProduction,
        hostname: window.location.hostname
    });

    // Enhanced signup flow detection
    const isSignupFlow = localStorage.getItem('signupInProgress') === 'true';
    const isSignupUrl = window.location.pathname.includes('/signup') ||
        window.location.pathname.includes('/get-started') ||
        window.location.pathname.includes('/sign-up');
    const isInSignupProcess = isSignupFlow || isSignupUrl;

    if (isInSignupProcess) {
        console.log('Suppressing token refresh during signup flow', {
            signupInProgress: isSignupFlow,
            signupUrl: isSignupUrl,
            currentPath: window.location.pathname
        });
        return Promise.reject(error);
    }

    // Handle 401 errors with unified approach
    if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then(token => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return apiClient(originalRequest);
            }).catch(err => {
                return Promise.reject(err);
            });
        }

        isRefreshing = true;

        try {
            // Get current Firebase user
            const { getAuth } = await import('firebase/auth');
            const auth = getAuth();
            const currentUser = auth.currentUser;

            if (!currentUser) {
                console.error('No Firebase user found for token refresh');
                clearAuthData();
                window.location.href = '/sign-in';
                return Promise.reject(error);
            }

            // Force token refresh
            console.log('Forcing token refresh for environment:', {
                isLocalDevelopment,
                isProduction
            });

            const newToken = await currentUser.getIdToken(true);
            localStorage.setItem('authToken', newToken);

            // Update axios default headers
            apiClient.defaults.headers.common.Authorization = `Bearer ${newToken}`;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;

            // Process queued requests
            processQueue(null, newToken);

            console.log('Token refreshed successfully');
            return apiClient(originalRequest);

        } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            processQueue(refreshError, null);
            clearAuthData();
            window.location.href = '/sign-in';
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }

    return Promise.reject(error);
};

// Single response interceptor to handle auth errors and token refresh
apiClient.interceptors.response.use(
    (response) => {
        // Log successful response for debugging (remove in production)
        if (process.env.NODE_ENV === 'development') {
            console.log('API Response:', {
                status: response.status,
                url: response.config.url,
                method: response.config.method?.toUpperCase()
            });
        }

        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Log error for debugging
        console.error('API Error:', {
            status: error.response?.status,
            url: error.config?.url,
            method: error.config?.method?.toUpperCase(),
            message: error.response?.data?.message || error.message
        });

        // Handle 401 Unauthorized (Authentication errors)
        if (error.response?.status === 401 && !originalRequest._retry) {
            return handleAuthenticationError(error, originalRequest);
        }

        // Handle 403 Forbidden (Authorization errors)
        if (error.response?.status === 403) {
            console.log('Authorization error detected');
            console.log('You do not have permission to perform this action.');

            // Don't redirect if we're already on dashboard
            if (!window.location.pathname.includes('/dashboard')) {
                setTimeout(() => {
                    window.location.href = '/dashboard';
                }, 2000);
            }

            return Promise.reject(error);
        }

        // Handle 404 Not Found
        if (error.response?.status === 404) {
            console.log('The requested resource was not found.');
        }

        // Handle 500 Internal Server Error
        if (error.response?.status === 500) {
            console.log('A server error occurred. Please try again later.');
        }

        // Handle network errors
        if (!error.response) {
            console.log('Network error. Please check your connection and try again.');
        }

        return Promise.reject(error);
    }
);

// Helper function to setup axios defaults
export const setupAxiosDefaults = () => {
    const token = localStorage.getItem('authToken');
    if (token) {
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
};

// Helper function to clear axios auth headers
export const clearAxiosAuth = () => {
    delete apiClient.defaults.headers.common['Authorization'];
    delete apiClient.defaults.headers.common['X-User-ID'];
};

// Helper function to update auth headers
export const updateAxiosAuth = (token, userId) => {
    if (token) {
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    if (userId) {
        apiClient.defaults.headers.common['X-User-ID'] = userId;
    }
};

// Export the configured axios instance
export default apiClient;

// Export a function to configure axios for the app
export const configureAxios = () => {
    // Setup initial defaults
    setupAxiosDefaults();

    // Set base URL if needed
    if (process.env.REACT_APP_API_BASE_URL) {
        apiClient.defaults.baseURL = process.env.REACT_APP_API_BASE_URL;
        console.log('Axios base URL set to:', process.env.REACT_APP_API_BASE_URL);
    }

    // Set default timeout - increased from 30s to 60s for slow backend responses
    apiClient.defaults.timeout = 60000; // 60 seconds

    // Set default headers
    apiClient.defaults.headers.common['Content-Type'] = 'application/json';

    console.log('Axios configured with interceptors and 60s timeout');
}; 